import type {
  Command,
  GameState,
  InteractionMode,
  LevelDef,
  Loadout,
  ReplayLog,
} from '@full-elevator/sim';
import {
  createInitialState,
  currentFloor,
  isParked,
  ReplayRecorder,
  step,
  summarize,
  TICK_MS,
} from '@full-elevator/sim';

/**
 * Bridges the deterministic simulation to real time.
 *
 * The simulation only ever advances in whole 20 ms ticks. This accumulates wall
 * time and spends it in fixed steps, so a dropped frame or a slow phone changes
 * how smoothly the game *looks* and never what it *does* — which is what keeps
 * a recorded replay valid across devices.
 */
export class Session {
  state: GameState;
  private accumulatorMs = 0;
  private recorder: ReplayRecorder;
  private pendingCommands: Command[] = [];
  /** Real time since the player last did anything. Drives the stall hint. */
  private idleMsSinceInput = 0;
  private realMs = 0;

  constructor(
    readonly level: LevelDef,
    readonly mode: InteractionMode,
    readonly seed = 1,
    readonly loadout?: Loadout,
  ) {
    this.state = createInitialState(level, {
      mode,
      seed,
      ...(loadout === undefined ? {} : { loadout }),
    });
    // The loadout goes into the log too: it changes how the run plays, so a
    // replay without it would not reproduce.
    this.recorder = new ReplayRecorder(level.id, mode, seed, this.state.loadout, level.lift?.id);
  }

  /** Queue a player action for the next tick. */
  send(command: Command): void {
    if (this.state.status !== 'playing') return;
    this.pendingCommands.push(command);
    this.idleMsSinceInput = 0;
  }

  /**
   * How long the player has gone without acting.
   *
   * Measured in wall time rather than simulation time on purpose: a player
   * staring at a level they do not understand is stuck in the real world, and
   * the hint that helps them should be on the same clock.
   */
  get idleMs(): number {
    return this.idleMsSinceInput;
  }

  /**
   * Wall time since the level opened, including any spent paused.
   *
   * Purely for animation. Never feed this into the simulation — the whole
   * determinism contract depends on the sim seeing only whole 20 ms ticks.
   */
  get elapsedRealMs(): number {
    return this.realMs;
  }

  /** Advance presentation-only clocks while the level itself is held still. */
  tickPresentation(deltaMs: number): void {
    this.realMs += Math.min(deltaMs, 250);
  }

  get finished(): boolean {
    return this.state.status !== 'playing';
  }

  get summary() {
    return summarize(this.state);
  }

  /**
   * Advance by real elapsed time. Large gaps (backgrounded tab, phone locked)
   * are clamped rather than fast-forwarded — resuming into a level that ran on
   * without you would be unreadable and unfair.
   */
  advance(deltaMs: number): void {
    this.tickPresentation(deltaMs);
    if (this.state.status !== 'playing') return;

    this.idleMsSinceInput += Math.min(deltaMs, 250);
    this.accumulatorMs += Math.min(deltaMs, 250);
    while (this.accumulatorMs >= TICK_MS && this.state.status === 'playing') {
      this.accumulatorMs -= TICK_MS;
      const commands = this.pendingCommands;
      this.pendingCommands = [];
      for (const command of commands) this.recorder.record(this.state.tick, command);
      step(this.state, this.level, commands);
    }
  }

  /** Where the car is, in fractional floors, for rendering. */
  get position(): number {
    return this.state.elevator.positionMilli / 1000;
  }

  get parkedFloor(): number | null {
    return isParked(this.state) ? currentFloor(this.state) : null;
  }

  /** The floor the doors are open on, or null. Gates passenger tapping. */
  get servicingFloor(): number | null {
    const { elevator } = this.state;
    if (elevator.phase !== 'servicing') return null;
    if (elevator.doorPhase !== 'open' && elevator.doorPhase !== 'opening') return null;
    return currentFloor(this.state);
  }

  toReplay(label?: string): ReplayLog {
    return this.recorder.finish(this.state, label);
  }
}
